import BottomDrawer from '@transferz/fe-component-library/src/components/bottomDrawer/BottomDrawer';
import { Button } from '@transferz/fe-component-library/src/components/button/Button';
import CircleXIcon from '@transferz/fe-component-library/src/components/icons/CircleXIcon';
import Typography from '@transferz/fe-component-library/src/components/typography/Typography';
import { JourneyCancellationReasonType, JourneyStatusType, PortalType } from '@transferz/fe-models';
import { useLocalize } from 'localize-react';
import React from 'react';

import { Loading } from '../../../../components/loading/Loading';
import { useMutationCancelJourney } from '../../../../queries/booking/hooks/useMutationCancelJourney';
import { isBeforeDate } from '../../../../utils/utils';
import styles from './cancel-booking.module.css';

interface ICancelBookingProps {
  isPopupOpen: boolean;
  params: any;
  setIsPopupOpen: Function;
  refetchJourney: Function;
  data: any
}

const getCancelationPortalTypeText = (data: any, isBefore: boolean, translate: any) => {
  const source = data?.booking?.creationInfo?.source;
  const partner = data?.booking?.partner;
  const isFreeCancellable = isBefore || data?.journey?.status === JourneyStatusType.Pending;

  if (isFreeCancellable) {
    if (source === PortalType.WhileLabel) {
      return translate(`booking_${source}`);
    } else if (partner?.communicationPreferences?.sendJourneyCancellationEmails) {
      return <>{translate('booking_emailPartnerForCancellations_1')} <b>{partner?.name}</b> {translate('booking_emailPartnerForCancellations_2')}</>;
    } else {
      return <>{translate('booking_api_1')} <b>{partner?.name}</b> {translate('booking_api_2')}</>;
    }
  }
  return translate('booking_cancellationWithCosts');
};

const getCancellationParams = (status: string, isBefore: boolean) => {
  const isFreeCancellable = isBefore || status === JourneyStatusType.Pending;
  if (isFreeCancellable) {
    return { acceptCharges: false, overrideCancellationWindow: isBefore && status === JourneyStatusType.Pending };
  } else {
    return { acceptCharges: true, overrideCancellationWindow: false };
  }
};

export const CancelBooking: React.FC<ICancelBookingProps> = ({
  params,
  isPopupOpen,
  setIsPopupOpen,
  refetchJourney,
  data,
}) => {
  const { translate } = useLocalize();
  const [cancelJourney, { loading: cancelJourneyIsLoading }] = useMutationCancelJourney();
  const status = data?.journey?.status || '';
  const isBefore = isBeforeDate(data?.journey?.cancellationDetails?.freeCancellationUntil as string);
  const portalTypeText = getCancelationPortalTypeText(data, isBefore, translate);
  const onCancelJourneyClicked = () => {
    cancelJourney({
      variables: {
        params: {
          id: data?.journey?.id,
          reason: JourneyCancellationReasonType.NotNeededAnymore,
          ...getCancellationParams(status, isBefore),
        },
      },
    })
      .then(() => {
        setIsPopupOpen(false);
        refetchJourney({
          variables: {
            id: Number(params.journeyId),
            bookingCode: params.bookingCode,
            includeCancellationDetails: true,
          },
        });
      });
  };

  if (cancelJourneyIsLoading) {
    return <Loading />;
  }

  return (
    <div className="cancel-booking">
      <BottomDrawer
        title={translate('booking_cancelTitle')}
        isOpen={isPopupOpen}
        onClose={() => setIsPopupOpen(false)}
        enableDesktopView={true}
      >
        <div className={styles.cancelBookingContent}>
          <ul>
            <li>
              <Typography variant="body1">{translate('booking_cancellationFirstText')}</Typography>
            </li>
            <li>
              <Typography variant="body1">{portalTypeText}</Typography>
            </li>
          </ul>
        </div>
        <div className={`${styles.cancelBookingFooter} d-flex justify-content-flex-end`}>
          <Button
            className={`mr-16 ${styles.closeWindow}`}
            label={translate('booking_closeWindow')}
            priority="secondary"
            onClick={() => setIsPopupOpen(false)}
            data-button-id="cancel-cancel-journey"
          />
          <Button
            className={styles.cancelBookingBtn}
            label={translate('booking_cancelBtn')}
            priority="primary"
            icon={<CircleXIcon width={15} color="light" />}
            onClick={onCancelJourneyClicked}
            variant="cancel"
            data-button-id="confirm-cancel-journey"
            fullWidth
          />
        </div>
      </BottomDrawer>
    </div>
  );
};
